import { useEffect, useState } from "react";
import { t } from "../../translations";

import { Aa } from "../functions/Aa";
import "../styles/section-chef.css";

import { GET, UPDATE } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";

import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { CircularProgress, Dialog, DialogContent, Rating } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { POST_USER_DATA } from "../../store/action";
import { openNotificationWithIcon } from "./notification-bar";

import { hasPassed15Minutes } from "../functions/dateFunctions";

import { createLogger } from "../../utils/logger";
import { getRefreshToken } from "../../utils/tokenUtils";

const logger = createLogger("SelectUserOrder");

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0
  },
  "&:before": {
    display: "none"
  }
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)"
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1)
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)"
}));

const SelectUserOrder = (props) => {
  const { user, lang } = props;

  const [load, setLoad] = useState(false);

  // Redux selectors and dispatch
  const userData = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const post_user_data = (data) => {
    dispatch({ type: POST_USER_DATA, data });
  };

  const [open, setOpen] = useState(false);

  const [order, setOrder] = useState([]);

  const [selectedOrder, setSelectedOrder] = useState(null);

  const [selectedValue, setSelectedValue] = useState("upcoming");

  const [expanded, setExpanded] = useState("");

  const [rate_food, setRateFood] = useState({
    text: "",
    rate: 5
  });

  const [rate_driver, setRateDriver] = useState({
    text: "",
    rate: 5
  });

  const setting = useSelector((state) => state.setting);

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (parseInt(status)) {
      case 0:
        return "#dec614"; // Waiting
      case 1:
        return "#3a7fcd"; // Ready
      case 2:
        return "#f66213"; // On Way
      case 3:
        return "#2dc144"; // Completed
      case 4:
        return "#ff114d"; // Canceled
      case 5:
        return "#F56417"; // Waiting for Refund
      case 6:
        return "#c12dc1"; // Refunded
      default:
        return "#c12dc1"; // Default
    }
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (parseInt(status)) {
      case 0:
        return t("common.waiting", lang);
      case 1:
        return t("common.ready", lang);
      case 2:
        return t("common.on_way", lang);
      case 3:
        return t("common.completed", lang);
      case 4:
        return t("buttons.canceled", lang);
      case 5:
        return t("common.waiting_for_ref", lang);
      case 6:
        return t("common.refunded", lang);
      default:
        return t("common.unknown", lang);
    }
  };

  useEffect(() => {
    initialData();
  }, []);

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const initialData = async () => {
    setLoad(true);

    // Get refresh token using utility function
    const currentRefreshToken = await getRefreshToken(userData, post_user_data);

    const request_details_orders = {
      type: GET,
      url: service_api,
      route: `order/get-user-orders/${user?._id}`,
      data: null,
      token: currentRefreshToken
    };

    try {
      const response = await MakeRequestAsync(request_details_orders);
      const decryptedData = decryptSingleData(response.data.order);
      setOrder(decryptedData);
    } catch (err) {
      logger.log("We couldn't fetch data: ", err?.response?.data?.message);
    } finally {
      setLoad(false);
    }
  };

  const submitReviewOrder = async (id) => {
    setLoad(true);

    // Get refresh token using utility function
    const currentRefreshToken = await getRefreshToken(userData, post_user_data);

    const request_details_orders = {
      type: UPDATE,
      url: service_api,
      route: `order/review-user-order/${id}`,
      data: {
        rate_food,
        rate_driver
      },
      token: currentRefreshToken
    };
    try {
      await MakeRequestAsync(request_details_orders);
      openNotificationWithIcon("success", t("success.review_submitted", lang));
      // Refresh the data instead of reloading the page
      initialData();
    } catch (err) {
      logger.log("We couldn't fetch data: ", err?.response?.data?.message);
      return openNotificationWithIcon(
        "error",
        t("errors.rate_order_error", lang)
      );
    } finally {
      setLoad(false);
    }
  };

  const askForRefundOnOrderCancelling = async (id) => {
    setLoad(true);

    // Get refresh token using utility function
    const currentRefreshToken = await getRefreshToken(userData, post_user_data);

    const request_details_orders = {
      type: UPDATE,
      url: service_api,
      route: `order/update-order-user/${id}`,
      data: {
        status: 5
      },
      token: currentRefreshToken
    };

    try {
      await MakeRequestAsync(request_details_orders);
      openNotificationWithIcon("success", t("success.order_cancelled", lang));
      // Refresh the data instead of reloading the page
      initialData();
    } catch (err) {
      logger.log("We couldn't fetch data: ", err?.response?.data?.message);
      return openNotificationWithIcon(
        "error",
        t("errors.rate_order_error", lang)
      );
    } finally {
      setLoad(false);
    }
  };

  function formatDateToYYYYMMDD(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is zero-indexed, so we add 1
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return (
    <div className="section-chef-menu-right-profil">
      <div className="section-chef-menu-right-profil-inside">
        <div
          className="section-chef-menu-right-profil-inside-box"
          style={{
            marginTop: 50
          }}
        >
          <div
            className="section-chef-menu-right-profil-inside-left"
            style={{
              width: "100%"
            }}
          >
            <p

            >
              {t("commerce.my_orders", lang)}
            </p>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                gap: 10
              }}
            >
              {[
                {
                  text: t("common.past", lang),
                  value: "previous"
                },
                {
                  text: t("common.upcoming", lang),
                  value: "upcoming"
                }
              ].map((item) => (
                <p
                  style={{
                    fontSize: 18,
                    width: "47%",
                    textAlign: "center",
                    padding: 10,
                    borderBottom: `3px solid ${
                      selectedValue === item?.value ? "#ff114d" : "#fff"
                    }`,
                    cursor: "pointer"
                  }}
                  onClick={() => {
                    setSelectedValue(item.value);
                  }}
                >
                  {item?.text}
                </p>
              ))}
            </div>

            <div
              style={{
                width: "100%",
                border: "1px solid #eeeeee",
                padding: 10,
                borderRadius: 5,
                marginTop: 15
              }}
            >
              <p
                style={{
                  fontSize: 16,
                  fontWeight: "600"
                }}
              >
                {t("commerce.list_of_orders", lang)}
              </p>
              {load && <CircularProgress size={40} />}
              {order
                ?.filter((item) => {
                  const currentDate = new Date();
                  const createdDate = new Date(
                    item?.billing_details?.delivery_date
                  );

                  const formattedCurrentDate =
                    formatDateToYYYYMMDD(currentDate);
                  const formattedCreatedDate =
                    formatDateToYYYYMMDD(createdDate);

                  if (selectedValue === "previous") {
                    // Calculate the current date
                    return formattedCreatedDate < formattedCurrentDate;
                  }

                  return formattedCreatedDate >= formattedCurrentDate;
                })
                .map((item, index) => (
                  <div
                    onClick={() => {}}
                    style={{
                      width: "100%",
                      marginTop: 20
                    }}
                  >
                    <Accordion
                      expanded={expanded === `panel${index + 1}`}
                      onChange={handleChange(`panel${index + 1}`)}
                      onClick={() => {
                        setSelectedOrder(item._id);
                      }}
                    >
                      <AccordionSummary
                        aria-controls="panel1d-content"
                        id="panel1d-header"
                      >
                        <Typography
                          style={{
                            fontSize: 15
                          }}
                        >
                          {t("common.delivery_date", lang)}{" "}
                          <b>{item?.billing_details?.delivery_date}</b> |{" "}
                          {t("commerce.order_from", lang)} Chef{" "}
                          {Aa(
                            item?.chef_id?.firstname +
                              " " +
                              item?.chef_id?.lastname
                          )}{" "}
                          <b>{"($" + item?.billing_details?.amount + ")"}</b> |{" "}
                          <span
                            style={{
                              backgroundColor: getStatusColor(item?.status),
                              color: "#fff",
                              padding: 7,
                              borderRadius: 20,
                              fontSize: 13
                            }}
                          >
                            {getStatusText(item?.status)}
                          </span>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {parseInt(item?.status) > 2 &&
                          item?.rate_food?.rate !== null && (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 10
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  fontWeight: "600",
                                  textAlign: "left"
                                }}
                              >
                                {t(
                                  "common.review_on_chef",
                                  lang,
                                  user?.restaurant
                                )}{" "}
                                <Rating
                                  readOnly
                                  value={item?.rate_food?.rate}
                                  // readOnly
                                  precision={1}
                                  style={{}}
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  fontWeight: "600",
                                  textAlign: "left"
                                }}
                              >
                                {t("common.review_on_deliv", lang)}{" "}
                                <Rating
                                  readOnly
                                  value={item?.rate_driver?.rate}
                                  // readOnly
                                  precision={1}
                                  style={{}}
                                />
                              </div>
                            </div>
                          )}
                        {parseInt(item?.status) === 3 &&
                          item?.rate_food?.rate === null && (
                            <div
                              style={{
                                width: 170,
                                backgroundColor: "#2dc144",
                                color: "#fff",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: 5,
                                borderRadius: 20,
                                cursor: "pointer"
                              }}
                              onClick={() => {
                                setOpen(true);
                              }}
                            >
                              {t("commerce.rate_your_order", lang)}{" "}
                            </div>
                          )}
                        <Typography
                          style={{
                            fontSize: 15,
                            marginTop: 25
                          }}
                        >
                          <b>{t("common.food_list", lang)}</b>
                        </Typography>
                        {item?.food.map((item) => (
                          <div
                            style={{
                              display: "flex",
                              width: "100%",
                              flexWrap: "wrap",
                              marginTop: 10
                            }}
                          >
                            <a
                              href={item?.pic}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                alt="food-pic"
                                src={item?.pic}
                                style={{
                                  width: 70,
                                  height: 70,
                                  borderRadius: 10,
                                  objectFit: "cover"
                                }}
                              />
                            </a>
                            <div
                              style={{
                                textAlign: "left"
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 15,
                                  marginLeft: 10
                                }}
                              >
                                <b>
                                  {item?.name}{" "}
                                  <span
                                    style={{
                                      color: "#000",
                                      border: "1px solid #cecece",
                                      padding: 7,
                                      borderRadius: 20,
                                      fontSize: 13
                                    }}
                                  >
                                    {item?.ingredient
                                      ?.map((e) =>
                                        lang === "fr" ? e.name : e.eng_name
                                      )
                                      .join(", ")}
                                  </span>
                                </b>
                              </span>
                              <br />
                              <span
                                style={{
                                  fontSize: 15,
                                  marginLeft: 10
                                }}
                              >
                                <b> Qty:{item?.qty}</b>
                              </span>
                              <br />
                              <span
                                style={{
                                  fontSize: 15,
                                  marginLeft: 10,
                                  color: "#ff114d"
                                }}
                              >
                                <b>{"$" + item?.cost * item?.qty}</b>
                              </span>{" "}
                              <br />
                            </div>
                          </div>
                        ))}
                      </AccordionDetails>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          paddingLeft: 10,
                          paddingBottom: 5
                        }}
                      >
                        <p
                          style={{
                            fontSize: 15,
                            fontWeight: "bold"
                          }}
                        >
                          {t("common.cancel_order_time", {
                            minutes: setting?.max_cancellation_waiting_time
                          })}
                        </p>
                        {hasPassed15Minutes(item.createdAt) === false &&
                          parseInt(item?.status) === 0 && (
                            <button
                              onClick={() => {
                                askForRefundOnOrderCancelling(item._id);
                              }}
                              className="btn-get-started"
                              style={{
                                width: 100,
                                backgroundColor: "#ff114d",
                                color: "#fff",
                                marginTop: 5,
                                padding: 7,
                                borderRadius: 20,
                                fontSize: 13
                              }}
                            >
                              {t("buttons.cancel", lang)}
                            </button>
                          )}
                      </div>
                    </Accordion>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}

      >
        <DialogContent>
          <div
            style={{
              minWidth: 300,
              height: "100%"
            }}
          >
            <div>
              <p
                style={{
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 15,
                  fontWeight: 600,
                  marginTop: 30,
                  fontSize: 17
                }}
              >
                {t("common.rating", lang)}
              </p>
              <p
                style={{
                  textAlign: "left",
                  fontSize: 15
                }}
              >
                {t("common.rate_your_food_", lang)}{" "}
              </p>
              {/* {JSON.stringify(address)} */}
              <div className="section-chef-menu-right-address-inside-box">
                <div
                  onClick={() => {}}
                  style={{
                    width: "100%",
                    borderRadius: 10,
                    border: "1px #cecece solid",
                    padding: 10
                  }}
                >
                  <Rating
                    value={rate_food.rate}
                    onChange={(e) => {
                      setRateFood({
                        ...rate_food,
                        rate: e.target.value
                      });
                    }}
                    // readOnly
                    precision={1}
                    style={{}}
                  />
                  <input
                    style={{
                      height: 40
                    }}
                    className="select-address-box"
                    placeholder={t(
                      "common.review_on_chef_1",
                      lang,
                      user?.restaurant
                    )}
                    // placeholderStyle
                    name="text"
                    value={rate_food.text}
                    type={"text"}
                    onChange={(e) => {
                      // e.preventDefault();
                      setRateFood({
                        ...rate_food,
                        text: e.target.value
                      });
                    }}
                  />
                </div>

                <div
                  onClick={() => {}}
                  style={{
                    width: "100%",
                    borderRadius: 5,
                    border: "1px #cecece solid",
                    padding: 5,
                    marginTop: 10
                  }}
                >
                  <Rating
                    value={rate_driver.rate}
                    onChange={(e) => {
                      setRateDriver({
                        ...rate_driver,
                        rate: e.target.value
                      });
                    }}
                    // readOnly
                    precision={1}
                    style={{}}
                  />
                  <input
                    style={{
                      height: 40
                    }}
                    className="select-address-box"
                    placeholder={t("common.review_on_deliv_1", lang)} // placeholderStyle
                    name="text"
                    value={rate_driver.text}
                    type={"text"}
                    onChange={(e) => {
                      // e.preventDefault();
                      setRateDriver({
                        ...rate_driver,
                        text: e.target.value
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className="btn-get-started"
              style={{
                backgroundColor: "#ff114d",
                width: "100%"
              }}
              onClick={() => {
                if (load) {
                  return;
                }

                submitReviewOrder(selectedOrder);
              }}
            >
              {!load && t("buttons.submit_2", lang)}
              {load && <CircularProgress size={25} />}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelectUserOrder;
